import { workspace } from './api';
import { logger } from '../helpers/logger';
import { amplitudeModule } from './amplitude';
import { RQE } from '../interfaces/users';

export interface Banner {
  id: number;
  name: string;
  filePath: string;
  url: string;
  link?: string;
  isMobile: boolean;
}

export interface Product {
  id: number;
  productType: string;
  campaignId: number;
  banner: Banner;
}

export interface Campaign {
  id: number;
  name: string;
  status: string;
  active: boolean;
  products: Product[];
}

export interface BannerData {
  mobile: string;
  desktop: string;
  alt: string;
  idBanner: number;
  link?: string;
}

export interface PrescriptionParams {
  prescriptionId: number;
  doctorId: number;
}

export interface CampaignFilters {
  partner: string[];
  speciality?: string[];
  age?: string;
  prescriptionParams?: PrescriptionParams;
}

export const fetchActiveCampaigns = async (filters: CampaignFilters): Promise<Campaign[]> => {
  try {
    const response = await workspace.post<Campaign[]>('/campaigns/find-by-product/banner', filters);
    return response.data;
  } catch (error) {
    logger.error('Erro ao buscar campanhas:', error);
    throw error;
  }
};

export const processActiveBanners = (campaigns: Campaign[]): BannerData[] => {
  const bannerData: BannerData[] = [];

  campaigns.forEach((campaign) => {
    if (campaign.active) {
      let mobileBanner: Banner | null = null;
      let desktopBanner: Banner | null = null;
      let bannerLink: string | undefined;

      campaign.products.forEach((product) => {
        if (product.productType === 'banner' && product.banner) {
          if (product.banner.isMobile) {
            mobileBanner = product.banner;
          } else {
            desktopBanner = product.banner;
          }

          if (product.banner.link) {
            bannerLink = product.banner.link;
          }
        }
      });

      if (mobileBanner || desktopBanner) {
        bannerData.push({
          mobile: mobileBanner ? (mobileBanner as Banner).url : '',
          desktop: desktopBanner ? (desktopBanner as Banner).url : '',
          alt: desktopBanner ? (desktopBanner as Banner).name : mobileBanner ? (mobileBanner as Banner).name : 'Banner',
          link: bannerLink,
          idBanner: campaign.id,
        });
      }
    }
  });

  return bannerData;
};

const BANNER_CAMPAIGN_FLAG_PREFIX = 'banner_campaign_';

export const filterBannersByExperiment = (banners: BannerData[]): BannerData[] => {
  if (!amplitudeModule.isExperimentInitialized()) return banners;

  try {
    const variants = amplitudeModule.getAllVariants();
    const gatedBanners = new Map<number, boolean>();

    for (const flagKey of Object.keys(variants)) {
      if (!flagKey.startsWith(BANNER_CAMPAIGN_FLAG_PREFIX)) continue;

      const campaignIdSuffix = flagKey.slice(BANNER_CAMPAIGN_FLAG_PREFIX.length);
      if (campaignIdSuffix?.length === 0) continue;

      const campaignId = Number(campaignIdSuffix);
      if (!Number.isInteger(campaignId)) continue;

      const variant = variants[flagKey];
      const variantValue = variant?.value ?? variant?.key;
      gatedBanners.set(campaignId, variantValue === 'on');
    }

    if (gatedBanners.size === 0) return banners;

    return banners.filter((banner) => {
      if (!gatedBanners.has(banner.idBanner)) return true;
      return gatedBanners.get(banner.idBanner) === true;
    });
  } catch (error) {
    logger.error('Erro ao filtrar banners por experimento:', error);
    return banners;
  }
};

export const fetchActiveBanners = async (filters: CampaignFilters): Promise<BannerData[]> => {
  try {
    const campaigns = await fetchActiveCampaigns(filters);
    if (!campaigns || campaigns?.length === 0) return [];

    await amplitudeModule.whenExperimentsReady();
    return filterBannersByExperiment(processActiveBanners(campaigns));
  } catch (error) {
    logger.error('Erro ao buscar banners ativos:', error);
    return [];
  }
};

/**
 * RQEs já resolvidos nesta sessão, por médico.
 *
 * O conjunto não muda durante a prescrição e a chamada fica no caminho crítico da listagem de
 * modelos — é aguardada antes do GET /protocols —, então repeti-la a cada busca só somava um
 * round trip ao tempo até a lista aparecer. Falhas não entram no cache: uma indisponibilidade
 * momentânea não pode desligar a especialidade pelo resto da sessão.
 */
const doctorRqesCache = new Map<number, Promise<any>>();

/**
 * Busca os RQEs (registros de qualificação de especialista) de um médico.
 *
 * O resultado é memoizado por médico durante a sessão: os RQEs não mudam no curso de uma
 * prescrição e esta chamada fica no caminho crítico da listagem de modelos — é aguardada antes
 * do `GET /protocols` —, então repeti-la a cada busca só somaria um round trip ao tempo até a
 * lista aparecer. A promessa é memoizada, e não só o resultado, para que chamadas paralelas
 * compartilhem a mesma requisição em vez de errarem o cache.
 *
 * Indisponibilidade não é cacheada: uma falha momentânea não pode desligar o filtro por
 * especialidade pelo resto da sessão.
 *
 * @param doctorId Prescritor cujos RQEs serão buscados.
 * @returns Os RQEs do médico; lista vazia quando não há `doctorId` ou quando a busca falha —
 * o filtro por especialidade é opcional e nunca deve bloquear a listagem.
 */
export const fetchDoctorRqes = async (doctorId: number): Promise<any> => {
  if (!doctorId) return [];

  const cached = doctorRqesCache.get(doctorId);
  if (cached) return cached;

  const request = (async () => {
    try {
      const response = await workspace.get(`/medico/${doctorId}/rqes`);
      return response.data;
    } catch (error) {
      doctorRqesCache.delete(doctorId);
      logger.error('Erro ao buscar RQEs do médico:', error);
      return [];
    }
  })();

  doctorRqesCache.set(doctorId, request);
  return request;
};

/**
 * Limpa o cache de RQEs.
 *
 * Existe para teste: o cache é estado de módulo e vaza entre casos, fazendo um teste depender
 * da ordem de execução do anterior. Não chame em código de produção — o cache é intencional e
 * está no caminho crítico da listagem de modelos.
 *
 * @returns Nada; a próxima chamada a `fetchDoctorRqes` volta a bater no backend.
 *
 * @internal
 */
export const resetDoctorRqesCache = (): void => {
  doctorRqesCache.clear();
};

// Monta o array de especialidades (idRqe) do médico, com o mesmo comportamento usado no banner:
// usa os RQEs já carregados na prescrição e, quando não houver, busca via fetchDoctorRqes.
export const resolveDoctorSpeciality = async (
  doctorSpecialities: RQE[] | undefined,
  prescriberId?: number
): Promise<string[] | undefined> => {
  const rqes = doctorSpecialities?.map((rqe) => rqe.idRqe).filter((idRqe) => idRqe != null) as string[] | undefined;

  if (rqes?.length === 0 && prescriberId) {
    const rqeResponse = await fetchDoctorRqes(prescriberId);
    if (rqeResponse?.data?.length > 0) {
      return rqeResponse.data.map((rqe: any) => rqe.idRqe).filter((idRqe: string) => idRqe != null);
    }
    return undefined;
  }

  return rqes;
};
